<script>
import Layout from "@/router/layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import axios from "axios";
import Swal from "sweetalert2";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { ref, defineAsyncComponent } from "vue";
import moment from "moment";

/* ======================================
 * ASYNC COMPONENT
 * ====================================== */
const FormAddPeserta = defineAsyncComponent(() =>
  import("./add-perencanaan_peserta.vue")
);
const FormImportPeserta = defineAsyncComponent(() =>
  import("./add-import_perencanaan_peserta.vue")
);

export default {
  page: {
    title: "Tambah Data Perencanaan Pembelajaran",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    Datepicker,
    FormAddPeserta,
    FormImportPeserta,
  },

  data() {
    return {
      /* =====================================================
       * UI
       * ===================================================== */
      title: "Tambah Data Perencanaan Pembelajaran",
      items: [
        { text: "Data Pembelajaran", href: "/" },
        { text: "Perencanaan", href: "/data-pembelajaran/perencanaan" },
        { text: "Tambah Data Perencanaan Pembelajaran", active: true },
      ],

      /* =====================================================
       * REFERENSI
       * ===================================================== */
      ref_kode_pembelajaran: [],
      ref_strategi: [],
      pic_ref: [],

      isLoadingReferensi: true,
      isReferensiReady: false,

      kode_pembelajaran_selected: null,

      /* =====================================================
       * FORM
       * ===================================================== */
      nomor_surat: null,
      kode: null,
      judul: null,
      dasar_pelaksanaan: null,
      strategi: null,
      durasi_he: 0,
      durasi_jp: null,
      tanggal_mulai: ref(new Date()),
      tanggal_selesai: ref(new Date()),
      angkatan: null,
      pratikum: "Tidak",
      pic: null,

      /* =====================================================
       * PESERTA
       * ===================================================== */
      peserta_add: [],
      peserta_add_id: [],

      /* =====================================================
       * MODAL
       * ===================================================== */
      showFormAddPeserta: false,
      showFormImportPeserta: false,

      /* =====================================================
       * SUBMIT
       * ===================================================== */
      status_submit: null,
      isSubmitting: false,
      axiosCatchError: "",
    };
  },

  /* =====================================================
   * LIFECYCLE
   * ===================================================== */
  async mounted() {
    await this.loadAllReferensi();
  },

  methods: {
    /* =====================================================
     * UTIL
     * ===================================================== */
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    showProgress(title, percent, text) {
      Swal.fire({
        title,
        html: `
          <div class="mb-2">${text}</div>
          <div class="progress">
            <div class="progress-bar progress-bar-striped progress-bar-animated"
              style="width:${percent}%">
            </div>
          </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
      });
    },

    /* =====================================================
     * LOAD ALL REFERENSI (CENTRALIZED)
     * ===================================================== */
    async loadAllReferensi() {
      this.isLoadingReferensi = true;
      this.isReferensiReady = false;
      this.axiosCatchError = "";

      try {
        const [strategi, pic, kode] = await Promise.all([
          this.loadReferensiStrategi(),
          this.loadReferensiPIC(),
          this.loadReferensiKodePembelajaran(),
        ]);

        this.ref_strategi = strategi;
        this.pic_ref = pic;
        this.ref_kode_pembelajaran = kode;

        this.isReferensiReady = true;
      } catch (err) {
        this.axiosCatchError =
          err?.response?.data?.message ||
          "Gagal memuat data referensi. Silakan refresh halaman.";
      } finally {
        this.isLoadingReferensi = false;
      }
    },

    /* =====================================================
     * REFERENSI API
     * ===================================================== */
    async loadReferensiStrategi() {
      const res = await axios.get(
        process.env.VUE_APP_BACKEND_URL_VERSION + "referensi/ref-strategi",
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        }
      );
      return res.data.data.referensi;
    },

    async loadReferensiPIC() {
      const res = await axios.get(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "referensi/pic-pembelajaran",
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        }
      );
      return res.data.data.referensi;
    },

    async loadReferensiKodePembelajaran() {
      const res = await axios.get(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "referensi/kode-pembelajaran",
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        }
      );
      return res.data.data.referensi;
    },

    /* =====================================================
     * SELECT HANDLER
     * ===================================================== */
    onChangeKodePembelajaran(val) {
      if (!val) return;

      this.kode = val.kode;
      this.judul = val.judul;
      this.durasi_he = val.he;
      this.durasi_jp = val.jp;

      if (val.strategi) {
        this.strategi = val.strategi;
      }

      this.countDurasiSelesai();
    },

    /* =====================================================
     * PESERTA
     * ===================================================== */
    formTambahPeserta() {
      this.showFormAddPeserta = false;
      this.$nextTick(() => (this.showFormAddPeserta = true));
    },

    formImportPeserta() {
      this.showFormImportPeserta = false;
      this.$nextTick(() => (this.showFormImportPeserta = true));
    },

    initDataPeserta(dataPeserta) {
      if (!dataPeserta) return;

      const peserta = [].concat.apply([], JSON.parse(dataPeserta));
      peserta.forEach((v) => {
        if (!this.peserta_add_id.includes(v.peserta_data.user_id)) {
          this.peserta_add.push(v);
        }
      });

      this.peserta_add_id = this.peserta_add.map(
        (v) => v.peserta_data.user_id
      );

      this.showFormAddPeserta = false;
      this.showFormImportPeserta = false;
    },

    deleteRow(_, row) {
      this.peserta_add = this.peserta_add.filter((r) => r !== row);
    },

    verifAll() {
      this.peserta_add.forEach(
        (p) => (p.peserta_status = "TERVERIFIKASI")
      );
    },

    /* =====================================================
     * VALIDATION & SUBMIT
     * ===================================================== */
    validateBeforeSubmit() {
      const errors = [];

      if (!this.nomor_surat) errors.push("Nomor Surat wajib diisi");
      if (!this.kode) errors.push("Kode Pembelajaran wajib dipilih");
      if (!this.judul) errors.push("Judul Pembelajaran wajib diisi");
      if (!this.dasar_pelaksanaan)
        errors.push("Dasar Pelaksanaan wajib diisi");
      if (!this.strategi) errors.push("Strategi wajib dipilih");
      if (!this.durasi_he || this.durasi_he <= 0)
        errors.push("Durasi HE wajib diisi");
      if (!this.durasi_jp || this.durasi_jp <= 0)
        errors.push("Durasi JP wajib diisi");
      if (!this.angkatan) errors.push("Angkatan wajib diisi");
      if (!this.pic) errors.push("PIC wajib dipilih");

      if (errors.length) {
        Swal.fire({
          icon: "warning",
          title: "Data belum lengkap",
          html: `<ul style="text-align:left">${errors
            .map((e) => `<li>${e}</li>`)
            .join("")}</ul>`,
        });
        return false;
      }

      return true;
    },

    async submitWithProgress() {
      this.isSubmitting = true;

      this.showProgress("Memproses", 30, "🔍 Memeriksa kelengkapan data...");
      await this.delay(400);

      if (!this.validateBeforeSubmit()) {
        this.isSubmitting = false;
        return;
      }

      this.showProgress("Menyimpan", 85, "💾 Menyimpan data...");
      await this.delay(400);

      this.StoreData();
    },

    /* =====================================================
     * STORE
     * ===================================================== */
    StoreData() {
      axios
        .post(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "transactions/pembelajaran",
          {
            nomor_surat: this.nomor_surat,
            kode: this.kode,
            judul: this.judul,
            dasar_pelaksanaan: this.dasar_pelaksanaan,
            strategi: this.strategi?.ms_name,
            strategi_id: this.strategi?.ms_id,
            durasi_he: this.durasi_he,
            durasi_jp: this.durasi_jp,
            tanggal_mulai: moment(this.tanggal_mulai).format("YYYY-MM-DD"),
            tanggal_selesai: moment(this.tanggal_selesai).format("YYYY-MM-DD"),
            angkatan: this.angkatan,
            pratikum: this.pratikum,
            pic: this.pic?.pic_id,
            pic_user_id: this.pic?.pic_user_id,
            peserta: JSON.stringify(this.peserta_add),
            status_submit: this.status_submit,
            user_id: localStorage.getItem("session_user_id"),
          },
          {
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + localStorage.access_token,
            },
          }
        )
        .then(() => {
          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Data berhasil disimpan",
            timer: 2000,
            showConfirmButton: false,
          }).then(() =>
            this.$router.push({ name: "all-perencanaan-pembelajaran" })
          );
        })
        .finally(() => (this.isSubmitting = false));
    },

    /* =====================================================
     * HELPER
     * ===================================================== */
    countDurasiSelesai() {
      let tgl = new Date(this.tanggal_mulai);
      let i = 0;

      while (i < this.durasi_he) {
        tgl.setDate(tgl.getDate() + 1);
        if (![0, 6].includes(tgl.getDay())) i++;
      }

      this.tanggal_selesai = new Date(tgl.setDate(tgl.getDate() - 1));
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },

    changeStatusSubmit(type) {
      this.status_submit = type;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- ================= LOADING REFERENSI ================= -->
    <div
      v-if="isLoadingReferensi"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <b-spinner variant="primary" />
      <div class="text-muted mt-2">
        Memuat data referensi, mohon tunggu…
      </div>
    </div>

    <!-- ================= ERROR REFERENSI ================= -->
    <div v-else-if="axiosCatchError" class="container mt-3">
      <div class="alert alert-danger text-center">
        <i class="mdi mdi-alert-circle-outline me-1"></i>
        {{ axiosCatchError }}
        <div class="mt-2">
          <button class="btn btn-sm btn-danger" @click="loadAllReferensi">
            <i class="mdi mdi-refresh"></i> Coba Muat Ulang
          </button>
        </div>
      </div>
    </div>

    <!-- ================= FORM UTAMA ================= -->
    <b-form
      v-else
      class="p-2"
      @submit.prevent="submitWithProgress"
    >
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div class="row">

              <!-- ================= FORM PERENCANAAN ================= -->
              <div class="col-12">
                <b-card
                  class="border border-primary"
                  header-class="bg-transparent border-primary"
                >
                  <template #header>
                    <h5 class="my-0 text-primary">
                      <i class="mdi mdi-calendar-edit me-3"></i>
                      Formulir Perencanaan Pembelajaran
                    </h5>
                  </template>

                  <div class="card-body p-0">
                    <div class="row">

                      <!-- ================= KIRI ================= -->
                      <div class="col-6">
                        <b-form-group>
                          <template #label>
                            Nomor Surat <span class="text-danger">*</span>
                          </template>
                          <b-form-input v-model="nomor_surat" />
                        </b-form-group>

                        <b-form-group>
                          <template #label>
                            Kode Pembelajaran <span class="text-danger">*</span>
                          </template>
                          <v-select
                            class="form-control p-0"
                            :options="ref_kode_pembelajaran"
                            label="kode"
                            v-model="kode_pembelajaran_selected"
                            placeholder="Pilih Kode Pembelajaran"
                            @update:modelValue="onChangeKodePembelajaran"
                          />
                        </b-form-group>

                        <b-form-group>
                          <template #label>
                            Judul <span class="text-danger">*</span>
                          </template>
                          <b-form-input v-model="judul" />
                        </b-form-group>

                        <b-form-group>
                          <template #label>
                            Dasar Pelaksanaan <span class="text-danger">*</span>
                          </template>
                          <b-form-input v-model="dasar_pelaksanaan" />
                        </b-form-group>

                        <b-form-group>
                          <template #label>
                            Strategi <span class="text-danger">*</span>
                          </template>
                          <v-select
                            class="form-control p-0"
                            :options="ref_strategi"
                            label="ms_name"
                            v-model="strategi"
                            placeholder="Pilih Strategi"
                          />
                        </b-form-group>

                        <div class="row">
                          <div class="col-6">
                            <b-form-group>
                              <template #label>
                                Durasi HE <span class="text-danger">*</span>
                              </template>
                              <input
                                type="number"
                                class="form-control"
                                v-model="durasi_he"
                                @change="countDurasiSelesai"
                              />
                            </b-form-group>
                          </div>

                          <div class="col-6">
                            <b-form-group>
                              <template #label>
                                Durasi JP <span class="text-danger">*</span>
                              </template>
                              <b-form-input type="number" v-model="durasi_jp" />
                            </b-form-group>
                          </div>
                        </div>

                        <div class="row">
                          <div class="col-6">
                            <b-form-group>
                              <template #label>
                                Tanggal Mulai <span class="text-danger">*</span>
                              </template>
                              <datepicker
                                v-model="tanggal_mulai"
                                :first-day-of-week="1"
                                :disabled-week-days="[0, 6]"
                                :format="formatDate"
                                confirm
                                @internal-model-change="countDurasiSelesai"
                              />
                            </b-form-group>
                          </div>

                          <div class="col-6">
                            <b-form-group>
                              <template #label>
                                Tanggal Selesai <span class="text-danger">*</span>
                              </template>
                              <datepicker
                                v-model="tanggal_selesai"
                                :first-day-of-week="1"
                                :disabled-week-days="[0, 6]"
                                :format="formatDate"
                                confirm
                              />
                            </b-form-group>
                          </div>
                        </div>
                      </div>

                      <!-- ================= KANAN ================= -->
                      <div class="col-6">
                        <b-form-group>
                          <template #label>
                            PIC <span class="text-danger">*</span>
                          </template>
                          <v-select
                            class="form-control p-0"
                            :options="pic_ref"
                            label="pic_keterangan"
                            v-model="pic"
                            placeholder="Pilih PIC"
                          />
                        </b-form-group>

                        <b-form-group>
                          <template #label>
                            Angkatan <span class="text-danger">*</span>
                          </template>
                          <b-form-input v-model="angkatan" />
                        </b-form-group>

                        <b-form-group>
                          <template #label>
                            Praktikum <span class="text-danger">*</span>
                          </template>
                          <select class="form-select" v-model="pratikum">
                            <option value="Tidak">Tidak</option>
                            <option value="Ya">Ya</option>
                          </select>
                        </b-form-group>
                      </div>

                    </div>
                  </div>
                </b-card>
              </div>

              <!-- ================= PESERTA ================= -->
              <div class="col-12 mt-3">
                <b-card class="border border-success">
                  <template #header>
                    <div class="d-flex justify-content-between">
                      <h5 class="text-success mb-0">
                        <i class="mdi mdi-account-multiple me-2"></i>
                        Peserta Pembelajaran
                        <small class="text-muted">(Opsional)</small>
                      </h5>
                      <div>
                        <button class="btn btn-success btn-sm" @click="formTambahPeserta">
                          <i class="mdi mdi-plus"></i> Tambah
                        </button>
                        <button class="btn btn-info btn-sm ms-1" @click="formImportPeserta">
                          <i class="mdi mdi-account-search"></i> Cek Peserta
                        </button>
                        <button
                          class="btn btn-primary btn-sm ms-1"
                          @click="verifAll"
                          :disabled="!peserta_add.length"
                        >
                          <i class="mdi mdi-check-all"></i> Verif All
                        </button>
                      </div>
                    </div>
                  </template>

                  <div class="alert alert-info mb-2">
                    Pengisian peserta <strong>tidak wajib</strong> pada tahap perencanaan.
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered mb-0">
                      <thead class="bg-dark text-white text-center">
                        <tr>
                          <th>No</th>
                          <th>NIP</th>
                          <th>Nama</th>
                          <th>Status</th>
                          <th>Verif</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!peserta_add.length">
                          <td colspan="6" class="text-center text-muted">
                            Belum ada peserta
                          </td>
                        </tr>
                        <tr v-for="(row, i) in peserta_add" :key="i">
                          <td>{{ i + 1 }}</td>
                          <td>{{ row.peserta_data.username }}</td>
                          <td>{{ row.peserta_data.name }}</td>
                          <td class="text-center">
                            <span class="badge badge-soft-success">OK</span>
                          </td>
                          <td class="text-center">
                            <b-form-checkbox
                              v-model="row.peserta_status"
                              value="TERVERIFIKASI"
                              unchecked-value="BELUM TERVERIFIKASI"
                            />
                          </td>
                          <td class="text-center">
                            <button class="btn btn-danger btn-sm" @click="deleteRow(i, row)">
                              <i class="fa fa-minus"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-card>
              </div>

              <!-- ================= ACTION ================= -->
              <div class="col-12 text-end mt-3">
                <router-link
                  :to="{ name: 'all-perencanaan-pembelajaran' }"
                  class="btn btn-secondary"
                >
                  Kembali
                </router-link>

                <b-button
                  class="ms-2"
                  variant="warning"
                  type="submit"
                  :disabled="isSubmitting"
                  @click="changeStatusSubmit('draft')"
                >
                  Simpan Draft
                </b-button>

                <b-button
                  class="ms-2"
                  variant="primary"
                  type="submit"
                  :disabled="isSubmitting"
                  @click="changeStatusSubmit('menunggu')"
                >
                  Simpan & Ajukan
                </b-button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </b-form>

    <!-- ================= MODAL ================= -->
    <b-modal v-model="showFormAddPeserta" size="xl" hide-footer title="Form Tambah Peserta">
      <form-add-peserta @init-data-peserta="initDataPeserta" />
    </b-modal>

    <b-modal v-model="showFormImportPeserta" size="lg" hide-footer title="Form Import Peserta">
      <form-import-peserta @init-data-peserta="initDataPeserta" />
    </b-modal>
  </Layout>
</template>


